import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Distance,
  DistanceUnit,
  Duration,
  FitnessActivity as FitnessActivityData,
  GetStravaMeasurementSystemQuery,
} from '../../../../types/__generated__';
import useFullPageQuery from '../../../hooks/useFullPageQuery';
import { getMeasurementSystemFallback } from '../../../utils/measurementSystem';
import { notNull } from '../../../utils/typeGuards';
import getStravaMeasurementSystem from '../StravaTimelineEntry/GetStravaMeasurementSystem.gql';
import { FitnessActivity } from './FitnessActivity';
import { FitnessTable } from './FitnessTable';

import styles from './FitnessMessage.scss';

interface Props {
  message: string | null | undefined;
  caption: string;
  fitness: FitnessActivityData;
}

function displayPreferenceGuard(
  displayValue: any,
  value?: Duration | Distance | number | null,
) {
  return Boolean(value) ? displayValue : null;
}

export const FitnessMessage: FunctionComponent<Props> = ({
  message,
  caption,
  fitness: { activityType, duration, distance, elevation, steps, title = '' },
}) => {
  const { data: settingsData } = useFullPageQuery<
    GetStravaMeasurementSystemQuery
  >(getStravaMeasurementSystem);
  const measurementSystem =
    settingsData?.page?.application?.settings?.measurementSystem ||
    getMeasurementSystemFallback();
  const { t } = useTranslation('common', { useSuspense: false });

  const headings = [
    displayPreferenceGuard(t('settings.fitness.showDuration'), duration),
    displayPreferenceGuard(t('settings.fitness.showDistance'), distance),
    displayPreferenceGuard(t('settings.fitness.showElevation'), elevation),
    displayPreferenceGuard(t('settings.fitness.showSteps'), steps),
  ].filter(notNull);

  const elevationElement =
    elevation &&
    measurementSystem &&
    `${elevation.value} ${distanceUnitMap[elevation.unit]}`;

  const rows = [
    [
      displayPreferenceGuard(duration, duration),
      displayPreferenceGuard(distance, distance),
      displayPreferenceGuard(elevationElement, elevation),
      displayPreferenceGuard(steps, steps),
    ],
  ].map(row => row.filter(notNull));

  return (
    <div>
      {activityType ? (
        <FitnessActivity activityType={activityType} title={title ?? ''} />
      ) : null}
      <div className={styles.message}>{message}</div>
      <FitnessTable caption={caption} headings={headings} rows={rows} />
    </div>
  );
};

const distanceUnitMap = {
  [DistanceUnit.Metres]: 'm',
  [DistanceUnit.Feet]: 'ft',
};
